import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../app/hooks"
import { getClientDatatableAsync, deleteAllSelectedClientAsync, resetSelectedRecordObjectInState, setPageSize, setSortConfig, setSearchValue, setClientActiveTab, updateSearchPagination } from "./AccountClientSlice";
import AccountClientItem from "./AccountClientItem";
import AccountClientForm from "./AccountClientForm";
import { ShowSwalWithConfirmation } from '../../common/SweetAlert';
import AccountClientOffCanvas from '../../common/OffCanvas'; 
import { KTIcon, useDebounce } from '../../_metronic/helpers';
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { updatePageIdState, getPagePermissionsAsync } from '../../common/GlobalSlice';
import Select, { StylesConfig } from 'react-select';
import CustomPagination from "../../common/Pagination";
import { ShowToast } from "../../common/Toastr";
import { resetSelectedRecordObjectInState as resetContactObject } from "./clientContacts/ClientContactSlice";
import { resetSelectedRecordObjectInState as resetDepartmentObject } from "./clientDepartment/ClientDepartmentSlice";
import { paginationStyles, getValueFromResource } from "../../common/timeLiveFunction";
import Loader from "../../common/Loader";
import PageSizeSelect from "../../common/PageSizeSelect";
import * as Sentry from "@sentry/react";
import AccountClientFilters from "./AccountClientFilters";

const AccountClient = () => {
    const dispatch = useAppDispatch();
    const modelRef = useRef<any>(null);
    const { t } = useTranslation();
    const { 
        accountClientList, isReload, selectedRecord, currentPage, pageSize, totalItems, start, recordsFiltered, search, columnIndex, direction, isLoading, tab,
        filterClientName, filterClientNick, filterEmailAddress, filterCountryId, filterStatus 
    } = useAppSelector((state) => state.accountClient);
    
    const { pageId, menuList, IsList, IsAdd, IsEdit, IsDelete, manageTerminologyList } = useAppSelector((state) => state.globalState);
    const location = useLocation();
    const navigate = useNavigate();
    const options = [{ value: '10', label: '10' }, { value: '20', label: '20' }, { value: '30', label: '30' }, { value: '40', label: '40' }, { value: '50', label: '50' }];
    const [isFirstTimeLoad, setIsFirstTimeLoad] = useState(true);

    useLayoutEffect(() => {
        const fetchPagePermissions = async () => {
            if (pageId && pageId !== '') {
                const res = await dispatch(getPagePermissionsAsync(pageId));
                if (!res.payload.IsList) {
                    navigate('../error/403');
                }
            } else if (!pageId && menuList && menuList.length > 0) {
                const routeName = location.pathname.split('/').filter(Boolean)[0];
                const foundItem = menuList.find(item => item.SystemCategoryPageFrontEnd === routeName);
                if (foundItem) {
                    const res = await dispatch(getPagePermissionsAsync(foundItem.SystemSecurityCategoryPageId));
                    if (!res.payload.IsList) {
                        navigate('../error/403');
                    }
                } else {
                    navigate('../error/403');
                }
            }
        };
        fetchPagePermissions();
    }, [dispatch, location.pathname, menuList, navigate, pageId]);

    const [showOffCanvas, setShowOffCanvas] = useState(false);
    const [selectedOffCanvas, setSelectedOffCanvas] = useState({
        "name": t('Add Client'),
        "width": "700px",
        "componentName": <AccountClientForm t={t} />
    });

   
    const handleOnClickEvent = (selectedObject: any, Name: string, IsEditMode: boolean) => {
        if (Name === "ClientContact" && IsEditMode === false) {
            dispatch(resetContactObject());
        }
        else if (Name === "ClientDepartment" && IsEditMode === false) {
            dispatch(resetDepartmentObject());
        }
        else if (Name === "Clients") {
            if (IsEditMode === false) {
                dispatch(resetSelectedRecordObjectInState());
            }
        }
        setSelectedOffCanvas(selectedObject);
        setShowOffCanvas(true);
    }

   
    const getFilterPayload = (overrides = {}) => {
        return {
            currentPage: currentPage ?? 1,
            pageSize: pageSize ?? 10,
            start: start ?? 0,
            order: columnIndex ?? 0,
            orderDir: direction ?? "asc",
            search: search || " ",
            ClientName: filterClientName || "",
            ClientNick: filterClientNick || "",
            EmailAddress: filterEmailAddress || "",
            CountryId: filterCountryId ?? 0,
            IsDisabled: filterStatus ?? -1,
            ...overrides
        };
    };

    useEffect(() => {
        if (isReload) {
            dispatch(getClientDatatableAsync(getFilterPayload()));
        }
    }, [isReload]);

    useEffect(() => {
        dispatch(getClientDatatableAsync(getFilterPayload()))
            .unwrap()
            .then((originalPromiseResult) => { setIsFirstTimeLoad(false); })
            .catch((rejectedValueOrSerializedError) => { 
                Sentry.captureException(rejectedValueOrSerializedError);
            })
    }, [dispatch]);

    const onDeleteSelectedClick = () => {
        ShowSwalWithConfirmation('Are you sure you want to delete this record?', "")
            .then((result) => {
                if (result) {
                    dispatch(deleteAllSelectedClientAsync(accountClientList))
                        .unwrap()
                        .then((originalPromiseResult) => {
                            if (originalPromiseResult[0].StatusCode === "200") {
                                ShowToast("Client Deleted Successfully", 'Success', "success", "toast-top-right")
                            }
                            else {
                                ShowToast('error', originalPromiseResult[0].ResponseMessage, "error", "toast-top-right");
                            }
                        })
                        .catch((rejectedValueOrSerializedError) => {
                            Sentry.captureException(rejectedValueOrSerializedError);
                        })
                }
            })
            .catch((error) => {
            });
    }

    const handlePageChange = (pageNumber: any) => {
        const calculatedStart = (pageNumber - 1) * pageSize;
        dispatch(getClientDatatableAsync(getFilterPayload({ 
            currentPage: pageNumber, 
            start: calculatedStart 
        })));
    };
    
    const debouncedSearchTerm = useDebounce(search, 500);

    useEffect(() => {
        if (!isFirstTimeLoad) {
            dispatch(getClientDatatableAsync(getFilterPayload({ 
                currentPage: 1, 
                start: 0, 
                search: debouncedSearchTerm 
            })));
        }
    }, [debouncedSearchTerm]);

    const handleSearch = (event: any) => {
        const { value } = event.target;
        dispatch(updateSearchPagination(event.target.value));
        dispatch(setSearchValue(value))
    }

    const handlePageSizeChange = (event: any) => {
        const { value } = event;
        dispatch(setPageSize(Number(value)));
        dispatch(getClientDatatableAsync(getFilterPayload({ 
            currentPage: 1, 
            pageSize: Number(value), 
            start: 0 
        })));
    }

    return (
        <>
            {isLoading && <Loader />}
            <div className="card card-custom  admin-card">
                <div className="card-header card-drop sticky-top bg-white fixed-top">
                    <h3 className="card-title fw-bold headerTitle">
                        {getValueFromResource('Clients',dispatch, t)}
                    </h3>
                    <div className="card-toolbar align-items-center">
                        <div className="position-relative">
                            <input
                                type="text"
                                value={search}
                                onChange={(e: any) => handleSearch(e)}
                                placeholder={getValueFromResource('Search',dispatch, t)}
                                className="form-control h-35px w-200px fs-7 fw-normal pr-4"
                                data-kt-user-table-filter="search"
                            />
                            <i className="ki-duotone ki-magnifier fs-5 position-absolute top-50 translate-middle-y end-0" style={{ marginRight: '10px' }}>
                                <span className="path1"></span>
                                <span className="path2"></span>
                            </i>
                        </div>
                        
                        <div className='h-35px w-1px mx-1'></div>
                        <div className="d-flex align-items-center">
                            {(accountClientList.length > 0) ? (
                                <PageSizeSelect onChange={handlePageSizeChange} />
                            ) : null}
                        </div>

                        {accountClientList.length > 0 && (
                            <>
                                <div className='bullet bg-secondary h-35px w-1px mx-3'></div>
                                <div>
                                    <CustomPagination
                                        currentPage={currentPage}
                                        onPageChange={handlePageChange}
                                        pageSize={pageSize}
                                        totalItems={totalItems}
                                        start={(currentPage - 1) * pageSize}
                                        t={t}
                                        recordsFiltered={recordsFiltered}
                                    />
                                </div>
                            </>
                        )}
                        
                        <div className='bullet bg-secondary h-35px w-1px mx-3' style={{ display: accountClientList.every((item: any) => item.isSelected === 0) ? 'block' : 'none' }}></div>
                        
                       
                        <button 
                            id="kt_OffCanvas"
                            type="button"
                            className="btn btn-icon btn-active-light-dark btn-sm ms-0"
                            onClick={() => handleOnClickEvent({
                                "name": getValueFromResource("Filters", dispatch, t),
                                "width": "500px",
                                "componentName": <AccountClientFilters t={t} />
                            }, "Filters", false)}
                            style={{ display: accountClientList.every((item: any) => item.isSelected === 0) ? 'block' : 'none' }}
                        >
                            <i className="bi bi-filter fs-3 iconColor"></i>
                        </button>

                        {(IsAdd || (IsDelete && accountClientList.some((item: any) => item.isSelected === 1))) ? (
                            <div className='bullet bg-secondary h-35px w-1px mx-3'></div>
                        ) : null}
                        
                        {IsAdd &&
                            <button id="kt_OffCanvas" type="button"
                                className="btn btn-primary btn-bs-btn-font-weight btn-sm"
                                onClick={() => handleOnClickEvent({
                                    "name": getValueFromResource('Add Client', dispatch, t),
                                    "width": "700px",
                                    "componentName": <AccountClientForm t={t} />
                                }, "Clients", false)}
                                style={{ marginRight: '2px', display: accountClientList.every((item: any) => item.isSelected === 0) ? 'block' : 'none' }}
                            >
                                {getValueFromResource('Add Client',dispatch, t)}
                            </button>
                        }
                        {IsDelete &&
                            <>
                                <div className="text-muted me-5" style={{ marginRight: '2px', display: accountClientList.some((item: any) => item.isSelected === 1) ? 'block' : 'none' }}>
                                    <span className="me-2" data-kt-user-table-select="selected_count">{accountClientList.filter((item: any) => item.isSelected === 1).length}</span>
                                    {getValueFromResource('Selected',dispatch, t)}
                                </div>
                                <button type="button" className="btn btn-danger btn-sm" onClick={() => onDeleteSelectedClick()}
                                    style={{ marginRight: '2px', display: accountClientList.some((item: any) => item.isSelected === 1) ? 'block' : 'none' }}
                                >
                                    <i className="bi bi-trash fs-5 me-2"></i>
                                    {getValueFromResource('Delete',dispatch, t)}
                                </button>
                            </>
                        }
                    </div>
                </div>
                <div className="card-Report-body" >
                    <div className="table-responsive">
                        <AccountClientItem t={t} IsEdit={IsEdit} IsDelete={IsDelete} onhandleOnClickEvent={handleOnClickEvent}
                            IsList={IsList}
                        />
                    </div>
                </div>
            </div>
            <AccountClientOffCanvas
                isOpen={showOffCanvas}
                onClose={() => setShowOffCanvas(false)}
                obj={selectedOffCanvas}
            />
        </>
    )
}

export default AccountClient;